import { FC, Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ArtistSkeleton } from 'components/ArtistView/Skeleton';
import { CollectionViewSkeleton } from 'components/CollectionView/Skeleton';
import { DrawerPage } from 'components/DrawerPage';
import { useHideHooryWidget } from 'hooks/third-party/useHooryWidget';
import { useAttachExternalFunctions } from './__partials/hooks/useAttachExternalFunctions';
import { useDefineInitialMountingPath } from './__partials/hooks/useDefineInitialMountingPath';
import { useInitialQueryChecker } from './__partials/hooks/useInitialQueryChecker';
import { useMessageListener } from './__partials/hooks/useMessageListener';
import { useTracker } from './__partials/hooks/useTracker';
import { CartView } from './__partials/views/CartView';
import { NewsLetterEmailUnsubscibe } from './__partials/views/NewsLetterEmailUnsubscribe';
import { NftCheckoutView } from './__partials/views/NftCheckout';
import { NftSingleView } from './__partials/views/NftSingleView';
import { UnsubscibeView } from './__partials/views/UnsubscribeView';
import { useAppSelector } from 'appRedux/hooks';
import { lazy } from 'utils/generic/lazy';
import { Configs } from 'constants/app/configs';
import {
  ARTIST_VIEW_URL_PATTERN,
  CLAIM_NFT_URL_PATTERN,
  COLLECTION_VIEW_URL_PATTERN,
  NEWS_LETTER_UNSUBSCRIBE_URL_PATTERN,
  UNSUBSCRIBE_URL_PATTERN
} from 'constants/app/route-reserved-words';
import { ViewModes } from 'interfaces';

const { CollectionView } = lazy(() => import('components/CollectionView'));

const { ArtistView } = lazy(() => import('components/ArtistView'));

const { ClaimNftModal } = lazy(() => import('elements/ClaimNFT/Modal'));

const { DefaultModeInitializer } = lazy(
  () =>
    import(
      /* webpackChunkName: "DefaultModeInitializer" */ './initializers/DefaultModeInitializer'
    )
);

const { KioskModeInitializer } = lazy(
  () =>
    import(
      /* webpackChunkName: "KioskModeInitializer" */ './initializers/KioskModeInitializer'
    )
);

const { FtnftAmModeInitializer } = lazy(
  () =>
    import(
      /* webpackChunkName: "FtnftAmModeInitializer" */ './initializers/FtnftAmModeInitializer'
    )
);

const INITIALIZER = {
  [ViewModes.DEFAULT]: DefaultModeInitializer,
  [ViewModes.KIOSK]: KioskModeInitializer,
  [ViewModes.FTNFT_AM]: FtnftAmModeInitializer,
  [ViewModes.KIOSK_AM]: KioskModeInitializer,
  [ViewModes.KIOSK_VENICE]: KioskModeInitializer
};

export const Common: FC = () => {
  useAttachExternalFunctions();
  useInitialQueryChecker();
  useMessageListener();
  useTracker();
  useHideHooryWidget();
  useDefineInitialMountingPath();
  const { pathname } = useLocation();
  const { error: userError, loaded: userLoaded } = useAppSelector(
    ({ user }) => user
  );
  const Initializer = INITIALIZER[Configs.VIEW_MODE];

  const showCollectionView = useMemo(
    () => COLLECTION_VIEW_URL_PATTERN.test(pathname),
    [pathname]
  );

  const showArtistView = useMemo(
    () => ARTIST_VIEW_URL_PATTERN.test(pathname),
    [pathname]
  );

  const showClaimNftModal = useMemo(() => {
    return CLAIM_NFT_URL_PATTERN.test(pathname);
  }, [pathname, userError, userLoaded]);

  const showUnsubscribeView = useMemo(
    () => UNSUBSCRIBE_URL_PATTERN.test(pathname),
    [pathname]
  );

  const showUnsubscribeNewsLetterView = useMemo(
    () => NEWS_LETTER_UNSUBSCRIBE_URL_PATTERN.test(pathname),
    [pathname]
  );

  return (
    <>
      <DrawerPage
        open={showCollectionView}
        headerStyle={{ position: 'static' }}
      >
        <Suspense fallback={<CollectionViewSkeleton />}>
          <CollectionView />
        </Suspense>
      </DrawerPage>
      <DrawerPage open={showArtistView} headerStyle={{ position: 'static' }}>
        <Suspense fallback={<ArtistSkeleton />}>
          <ArtistView />
        </Suspense>
      </DrawerPage>
      <NftCheckoutView />
      <NftSingleView />
      {showUnsubscribeView && <UnsubscibeView />}
      {showUnsubscribeNewsLetterView && <NewsLetterEmailUnsubscibe />}
      {Configs.CART_ENABLED && <CartView />}
      <ClaimNftModal open={showClaimNftModal} />
      <Suspense fallback={null}>
        <Initializer />
      </Suspense>
    </>
  );
};
